import { FormProvider, useForm } from "react-hook-form";
import { DynamicFormField, FieldType } from "@components/ui/DynamicInput";
import { loanFormFieldType } from "./formFieldsConfig";
import { Button } from "@components/shadcn-ui/button";
import { useEffect, useMemo } from "react";
import { FileUploadComponent } from "features/createApplication/components/FileUploadComponent";
import { MobileFileManager } from "features/createApplication/components/DisplayApplicationIDContent";
import { FileManager } from "features/createApplication/components/FileManager";
import { ApplicationDetailsPage } from "pages/dashboard/ApplicationDetailsPage";
import PermissionWrapper from "features/Permission/PermissionWrapper";
import { Action, ApplicationState } from "features/Permission/types";
import { AddressManager } from "features/createApplication/components/AddressManager";
import { BankManager } from "features/createApplication/components/BankManager";
import { ReferenceManager } from "features/createApplication/components/ReferenceManager";
import { CoApplicantManager } from "features/createApplication/components/CoApplicantManager";
import { useMultiStepForm } from "./context/MultiStepFormContext";
import { applicationService } from "features/createApplication/service/applicationService";

function NavigationButtons({
  currentStep,
  handleClick,
  goToStep,
  stepsLength,
  validateCurrentStep,
}: {
  currentStep: number;
  handleClick: (actionType: string) => void;
  goToStep: (step: number) => void;
  stepsLength: number;
  validateCurrentStep: () => Promise<boolean>;
}) {
  const handleNextSection = async () => {
    // Validate the current step before proceeding
    const isValid = await validateCurrentStep();
    if (isValid) {
      goToStep(currentStep + 1);
    }
  };

  return (
    <div className="flex justify-center mt-8 gap-4 mb-4">
      {currentStep >= 0 && (
        <>
          {currentStep !== 0 && (
            <Button
              onClick={() => {
                goToStep(currentStep - 1);
              }}
            >
              Go to previous section
            </Button>
          )}

          {currentStep === stepsLength - 3 ? (
            <Button onClick={() => handleClick("SAVE_APPLICATION")}>
              Save and Next
            </Button>
          ) : (
            currentStep <= stepsLength - 2 && (
              <Button onClick={handleNextSection}>
                Go to next section
              </Button>
            )
          )}
        </>
      )}
    </div>
  );
}

export const MultiStepLoanApplication = ({
  currentStep,
  goToStep,
  formFileds,
  handleAction,
  fieldDisableState,
  isMobile,
  applicationStatus,
  productType,
}: {
  currentStep: number;
  formFileds: loanFormFieldType[];
  handleAction: (actionType: string, values: any) => Promise<void>;
  goToStep: (step: number) => void;
  fieldDisableState: Record<string, boolean>;
  isMobile: boolean;
  applicationStatus: ApplicationState;
  productType: string;
}) => {
  const form = useForm({
    mode: "all",
  });
  const { 
    addressIds, 
    bankIds, 
    references, 
    setReferences, 
    coApplicants: contextCoApplicants, 
    setCoApplicants,
    leadId: contextLeadId 
  } = useMultiStepForm();

  const computedDefaultValues = useMemo(() => {
    return formFileds.reduce((acc: any, section: any) => {
      // Handle nested fields for sections like incomeDetails
      if (section.name === 'incomeDetails') {
        acc[section.name] = acc[section.name] || {};
        
        section.fields.forEach((field: FieldType) => {
          if (field.name === 'assets') {
            // Ensure assets is always an array
            acc[section.name][field.name] = field.defaultValue || [];
          } else if (field.name === 'monthlyIncome' || field.name === 'otherMonthlyIncome') {
            // Ensure income values are properly initialized as numbers, not as 0
            acc[section.name][field.name] = field.defaultValue !== undefined ? field.defaultValue : "";
          } else {
            acc[section.name][field.name] = field.defaultValue ?? "";
          }
        });
      } else {
        // Handle regular fields
        section.fields.forEach((field: FieldType) => {
          // Special handling for multiselect fields
          if (field.type === 'multiselect') {
            // Initialize multiselect fields as arrays
            acc[field.name] = field.defaultValue || [];
          } else {
            acc[field.name] = field.defaultValue ?? "";
          }
        });
      }
      return acc;
    }, {});
  }, [formFileds]);

  // Reset the form values when computedDefaultValues change
  useEffect(() => {
    // Log the default values for debugging
    console.log("Form default values:", computedDefaultValues);
    
    // Make a deep copy to avoid reference issues
    const modifiedDefaults = JSON.parse(JSON.stringify(computedDefaultValues));
    
    // Make sure incomeDetails exists and has proper structure
    if (modifiedDefaults.incomeDetails) {
      // Ensure assets is always an array
      modifiedDefaults.incomeDetails.assets = Array.isArray(modifiedDefaults.incomeDetails.assets) ? 
        modifiedDefaults.incomeDetails.assets : [];
    }
    
    form.reset(modifiedDefaults);
  }, [computedDefaultValues, form]);

  const currentSection = formFileds[currentStep];

  const handleClick = async (actionType: string) => {
    const isValid = await form.trigger();

    if (isValid) {
      if (actionType === "NEXT_STEP") {
        goToStep(currentStep + 1);
      } else if (actionType === "CREATE_APPLICATION") {
        await handleAction(actionType, form.getValues());
      } else if (actionType === "SAVE_APPLICATION" || actionType === "SUBMIT_APPLICATION") {
        // Include address IDs, bank IDs, references, and co-applicant in the form data
        const formValues = form.getValues();
        
        // Handle incomeDetails properly
        const incomeDetails = formValues.incomeDetails || {};
        
        // Ensure assets is always an array
        if (incomeDetails.assets && !Array.isArray(incomeDetails.assets)) {
          incomeDetails.assets = incomeDetails.assets ? [incomeDetails.assets] : [];
        }
        
        // Convert income values to numbers, but only if they're not empty strings
        // This prevents empty fields from becoming 0
        const monthlyIncome = incomeDetails.monthlyIncome !== "" && incomeDetails.monthlyIncome !== undefined
          ? parseFloat(incomeDetails.monthlyIncome)
          : incomeDetails.monthlyIncome;
          
        const _otherMonthlyIncomeSource = incomeDetails.otherMonthlyIncome;
        const otherMonthlyIncome =
          _otherMonthlyIncomeSource !== "" && _otherMonthlyIncomeSource !== undefined
            ? parseFloat(String(_otherMonthlyIncomeSource))
            : _otherMonthlyIncomeSource;
        
        const payload: any = { 
          ...formValues, 
          addresses: addressIds || [],
          bankAccounts: bankIds || [],
          references: references || [],
          incomeDetails: {
            ...incomeDetails,
            monthlyIncome: monthlyIncome,
            otherMonthlyIncome: otherMonthlyIncome,
            assets: incomeDetails.assets || []
          }
        };
        
        // Add coApplicantDetails array to payload if present in context (filter invalid/empty entries)
        const isValidCoApplicantLocal = (c: any) => {
          if (!c) return false;
          const firstName = c.personalDetails?.firstName?.trim() || "";
          const lastName = c.personalDetails?.lastName?.trim() || "";
          const hasName = firstName !== "" || lastName !== "";
          const hasKycValue = c.kycDetails && Object.values(c.kycDetails).some((v: any) => typeof v === "string" ? v.trim() !== "" : !!v);
          const hasAddress = Array.isArray(c.addressId) && c.addressId.some((id: any) => typeof id === "string" && id.trim() !== "");
          const hasRelation = typeof c.relationToApplicant === "string" && c.relationToApplicant.trim() !== "";
          return hasName || hasKycValue || hasAddress || hasRelation;
        };

        const validCoApplicantsForPayload = Array.isArray(contextCoApplicants) ? contextCoApplicants.filter(isValidCoApplicantLocal) : [];
        if (validCoApplicantsForPayload.length > 0) {
          payload.coApplicantDetails = validCoApplicantsForPayload;
          console.log("Adding co-applicants array to payload:", validCoApplicantsForPayload);
        }

        console.log("Final payload with co-applicant:", payload);
        await handleAction(actionType, payload);
      }
    } else {
      console.log("Form is invalid");
    }
  };

  // Function to validate the current step's fields
  const validateCurrentStep = async () => {
    const currentSection = formFileds[currentStep];
    
    // Skip validation for custom components or special sections
    if (currentSection.isCustomComponent || 
        currentSection.name === "documents" || 
        currentSection.name === "review") {
      return true;
    }
    
    // Get field names for the current step
    const fieldNames = currentSection.fields.map((field: FieldType) => {
      // Handle nested fields like incomeDetails.assets
      if (field.name === "monthlyIncome" || field.name === "otherMonthlyIncome" || field.name === "assets") {
        return `${currentSection.name}.${field.name}`;
      }
      return field.name;
    });
    
    // Trigger validation only for the current step's fields
    const result = await form.trigger(fieldNames);
    
    if (!result) {
      console.log("Validation failed for fields:", fieldNames);
    }
    
    return result;
  };

  const applicationId = form.watch("id");
  // Try multiple ways to get the leadId
  const watchedLeadId = form.watch("leadId");
  const formLeadId = form.getValues("leadId");
  
  // Use the first available leadId
  const leadId = watchedLeadId || formLeadId || contextLeadId;

  // Get the lead phone number for reference validation
  const leadPhoneNumber = form.watch("phoneNumber") || "";
  
  // Get the applicant PAN number for co-applicant validation
  const applicantPanNumber = form.watch("kycDetails.panNumber") || "";

  // Load existing references and co-applicant when application ID is available
  useEffect(() => {
    const loadExistingData = async () => {
      if (applicationId && applicationId !== "undefined" && applicationId !== "null") {
        try {
          console.log("Loading application data:", applicationId);
          const applicationData = await applicationService.getApplicationById(applicationId);
          
          if (applicationData && applicationData.references && applicationData.references.length > 0) {
            console.log("Found existing references:", applicationData.references);
            setReferences(applicationData.references);
          } else {
            // Reset references if none found
            setReferences([]);
          }
          
          // Normalize coApplicantDetails to an array and filter empty entries
          const normalizeCoApplicants = (input: any): any[] => {
            if (!input) return [];
            if (Array.isArray(input)) return input;
            // single object -> wrap into array
            return [input];
          };

          const rawCoApplicants = normalizeCoApplicants(applicationData?.coApplicantDetails);
          // helper to determine validity (same logic as CoApplicantManager)
          const isValidCoApplicant = (c: any) => {
            if (!c) return false;
            const firstName = c.personalDetails?.firstName?.trim() || "";
            const lastName = c.personalDetails?.lastName?.trim() || "";
            const hasName = firstName !== "" || lastName !== "";
            const hasKycValue = c.kycDetails && Object.values(c.kycDetails).some((v: any) => typeof v === "string" ? v.trim() !== "" : !!v);
            const hasAddress = Array.isArray(c.addressId) && c.addressId.some((id: any) => typeof id === "string" && id.trim() !== "");
            const hasRelation = typeof c.relationToApplicant === "string" && c.relationToApplicant.trim() !== "";
            return hasName || hasKycValue || hasAddress || hasRelation;
          };

          const validCoApplicants = rawCoApplicants.filter(isValidCoApplicant);
          setCoApplicants(validCoApplicants);

        } catch (error) {
          console.error("Error loading application data:", error);
        }
      }
    };
    
    loadExistingData();
  }, [applicationId, setReferences, setCoApplicants]);

  // Debug log to check values
  console.log("Application ID:", applicationId, "Lead ID sources:", { 
    watchedLeadId, 
    formLeadId, 
    contextLeadId,
    finalLeadId: leadId 
  });

  const handleReferencesUpdated = (updatedReferences: any[]) => {
    setReferences(updatedReferences);
  };
  
  const handleCoApplicantsUpdated = (updatedCoApplicants: any[]) => {
    // Ensure only valid co-applicants propagate to context
    setCoApplicants(updatedCoApplicants || []);
  };

  // Get all reference phone numbers for co-applicant validation
  const referencePhoneNumbers = references.map(ref => ref.personalDetails.phoneNumber);

  const renderCustomComponent = () => {
    if (!currentSection.customComponentName) return null;

    switch (currentSection.customComponentName) {
      case "AddressManager":
        // Ensure we have valid IDs before rendering the AddressManager
        console.log("Rendering AddressManager with:", { applicationId, leadId });
        
        // More permissive check - if we have either ID, try to render
        if ((!applicationId || applicationId === "undefined" || applicationId === "null") && 
            (!leadId || leadId === "undefined" || leadId === "null")) {
          console.error("Missing required IDs for AddressManager:", { applicationId, leadId });
          return (
            <div className="p-4 border border-red-300 bg-red-50 rounded-md">
              <p className="text-red-600">
                Application ID or Lead ID is missing. Please ensure you've completed the previous steps.
              </p>
            </div>
          );
        }
        
        return (
          <AddressManager
            applicationId={applicationId || ""}
            leadId={leadId || ""}
          />
        );
      
      case "BankManager":
        // Ensure we have valid IDs before rendering the BankManager
        console.log("Rendering BankManager with:", { applicationId, leadId });
        
        // More permissive check - if we have either ID, try to render
        if ((!applicationId || applicationId === "undefined" || applicationId === "null") && 
            (!leadId || leadId === "undefined" || leadId === "null")) {
          console.error("Missing required IDs for BankManager:", { applicationId, leadId });
          return (
            <div className="p-4 border border-red-300 bg-red-50 rounded-md">
              <p className="text-red-600">
                Application ID or Lead ID is missing. Please ensure you've completed the previous steps.
              </p>
            </div>
          );
        }
        
        return (
          <BankManager
            applicationId={applicationId || ""}
            leadId={leadId || ""}
          />
        );

      case "CoApplicantManager":
        // Ensure we have valid IDs before rendering the CoApplicantManager
        console.log("Rendering CoApplicantManager with:", { applicationId, leadId, leadPhoneNumber, applicantPanNumber });
        
        // More permissive check - if we have either ID, try to render
        if ((!applicationId || applicationId === "undefined" || applicationId === "null") && 
            (!leadId || leadId === "undefined" || leadId === "null")) {
          console.error("Missing required IDs for CoApplicantManager:", { applicationId, leadId });
          return (
            <div className="p-4 border border-red-300 bg-red-50 rounded-md">
              <p className="text-red-600">
                Application ID or Lead ID is missing. Please ensure you've completed the previous steps.
              </p>
            </div>
          );
        }
        
        return (
          <PermissionWrapper
            actions={Action.EDIT_APPLICATION}
            applicationStatus={applicationStatus}
            productType={productType}
          >
            {({ isAllowed }) => (
              <CoApplicantManager
                applicationId={applicationId || ""}
                leadId={leadId || ""}
                leadPhoneNumber={leadPhoneNumber}
                applicantPanNumber={applicantPanNumber}
                initialCoApplicants={contextCoApplicants}
                onCoApplicantsUpdated={handleCoApplicantsUpdated}
                readOnly={!isAllowed}
                existingReferencePhoneNumbers={referencePhoneNumbers}
              />
            )}
          </PermissionWrapper>
        );

      case "ReferenceManager":
        // Ensure we have valid IDs before rendering the ReferenceManager
        console.log("Rendering ReferenceManager with:", { applicationId, leadId, leadPhoneNumber });
        
        // More permissive check - if we have either ID, try to render
        if ((!applicationId || applicationId === "undefined" || applicationId === "null") && 
            (!leadId || leadId === "undefined" || leadId === "null")) {
          console.error("Missing required IDs for ReferenceManager:", { applicationId, leadId });
          return (
            <div className="p-4 border border-red-300 bg-red-50 rounded-md">
              <p className="text-red-600">
                Application ID or Lead ID is missing. Please ensure you've completed the previous steps.
              </p>
            </div>
          );
        }
        
        return (
          <PermissionWrapper
            actions={Action.EDIT_APPLICATION}
            applicationStatus={applicationStatus}
            productType={productType}
          >
            {({ isAllowed }) => (
              <ReferenceManager
                applicationId={applicationId || ""}
                leadId={leadId || ""}
                leadPhoneNumber={leadPhoneNumber}
                initialReferences={references}
                onReferencesUpdated={handleReferencesUpdated}
                readOnly={!isAllowed}
              />
            )}
          </PermissionWrapper>
        );
        
      default:
        return null;
    }
  };

  return (
    <FormProvider {...form}>
      <form
        className="flex items-center flex-col"
        onSubmit={(e) => {
          e.preventDefault();
        }}
      >
        <div key={currentStep} className="w-full">
          <div className="flex flex-wrap gap-x-6 pl-2 justify-center w-full gap-3">
            {currentSection.name === "documents" ? (
              <>
                <FileUploadComponent id={applicationId!} />
                <strong className="block mb-2 p-3text-md bg-[#bbd0ce] p-2 pl-4 rounded-sm w-full">
                  Upload Documents
                </strong>
                {isMobile ? (
                  <MobileFileManager applicationId={applicationId} />
                ) : (
                  <FileManager applicationId={applicationId} />
                )}
              </>
            ) : currentSection.name === "review" ? (
              <>
                <div className="review-section w-full">
                  <h2 className="text-lg font-bold mb-1 px-6 text-center mt-3">
                    Review Your Application
                  </h2>
                  <p className="mb-4 px-6 text-center">
                    Please review the details below before submitting your
                    application.
                  </p>
                  {/* Ideally we shouldn't use this page component here. */}
                  {/* TODO: create a common component to use on both the place */}
                  {/* TODO: Check if we can specify component name in the form fields configutation */}
                  <ApplicationDetailsPage appId={applicationId} />
                </div>
              </>
            ) : currentSection.isCustomComponent ? (
              renderCustomComponent()
            ) : (
              <>
                {isMobile && (
                  <strong className="w-full block mt-4 text-lg rounded-sm underline underline-offset-4">
                    {currentSection.section}
                  </strong>
                )}
                {currentSection.fields.map(
                  (field: FieldType, index: number) => {
                    const isFieldDisabled =
                      fieldDisableState[field.name] || false;
                      
                    // Special handling for nested fields like incomeDetails.assets
                    if (field.name === "monthlyIncome" || field.name === "otherMonthlyIncome" || field.name === "assets") {
                      // Register the field with the correct path
                      const fieldPath = `${currentSection.name}.${field.name}`;
                      
                      // Make sure the field is registered
                      if (field.name === "assets" && !form.getValues(fieldPath)) {
                        form.setValue(fieldPath, []);
                      }
                      
                      // Create a modified field with the correct path
                      const modifiedField = {
                        ...field,
                        name: fieldPath
                      };
                      
                      return (
                        <div
                          className="w-full flex flex-wrap md:basis-[45%] items-center"
                          key={index}
                        >
                          <PermissionWrapper
                            actions={Action.EDIT_APPLICATION}
                            applicationStatus={applicationStatus}
                            productType={productType}
                          >
                            {({ isAllowed }) => (
                              <DynamicFormField
                                field={modifiedField}
                                isDisabled={isFieldDisabled || !isAllowed}
                              />
                            )}
                          </PermissionWrapper>
                        </div>
                      );
                    }
                    
                    return (
                      <div
                        className="w-full flex flex-wrap md:basis-[45%] items-center"
                        key={index}
                      >
                        <PermissionWrapper
                          actions={Action.EDIT_APPLICATION}
                          applicationStatus={applicationStatus}
                          productType={productType}
                        >
                          {({ isAllowed }) => (
                            <DynamicFormField
                              field={field}
                              isDisabled={isFieldDisabled || !isAllowed}
                            />
                          )}
                        </PermissionWrapper>
                      </div>
                    );
                  }
                )}
              </>
            )}
          </div>
        </div>
        <NavigationButtons
          handleClick={handleClick}
          currentStep={currentStep}
          goToStep={goToStep}
          stepsLength={formFileds.length}
          validateCurrentStep={validateCurrentStep}
        />
      </form>
    </FormProvider>
  );
}
